
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  Collapse,
  InputAdornment,
  IconButton,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { colors } from '../../theme/theme';

interface AuthDialogsProps {
  openLogin: boolean;
  onCloseLogin: () => void;
  openRegister: boolean;
  onCloseRegister: () => void;
  onOpenRegister: () => void; // Pour basculer du login vers register
}

export const AuthDialogs: React.FC<AuthDialogsProps> = ({
  openLogin,
  onCloseLogin,
  openRegister,
  onCloseRegister,
  onOpenRegister
}) => {
  const { login, signup, resetPassword } = useAuth();

  // Login State
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);

  // Register State
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerError, setRegisterError] = useState('');
  const [registerProfile, setRegisterProfile] = useState({
    firstName: '',
    lastName: '',
    nickname: '',
    city: '',
    team: 'Mercenaire Solitaire',
    notificationsEnabled: true
  });

  // UI State
  const [showPassword, setShowPassword] = useState(false);

  const handleLogin = async () => {
    try {
      setLoginError('');
      await login(loginEmail, loginPassword);
      onCloseLogin();
    } catch (error: any) {
      if (error.code === 'auth/user-not-found') {
        setLoginError('Utilisateur inconnu. Veuillez vérifier votre email ou créer un compte.');
      } else if (error.code === 'auth/wrong-password') {
        setLoginError('Mot de passe incorrect.');
      } else if (error.code === 'auth/invalid-email') {
        setLoginError('Format d\'email invalide.');
      } else {
        setLoginError('Une erreur est survenue lors de la connexion.');
      }
      console.error('Error logging in:', error);
    }
  };

  const handleForgotPassword = async () => {
    if (!loginEmail) {
      setLoginError('Veuillez entrer votre adresse email');
      return;
    }
    try {
      await resetPassword(loginEmail);
      setResetEmailSent(true);
      setLoginError('');
      setTimeout(() => {
        setResetEmailSent(false);
      }, 10000);
    } catch (error: any) {
      if (error.code === 'auth/user-not-found') {
        setLoginError('Aucun compte associé à cet email');
      } else {
        setLoginError('Une erreur est survenue. Veuillez réessayer.');
      }
    }
  };

  const handleRegister = async () => {
    setRegisterError('');

    // Validation simple
    if (!registerEmail) { setRegisterError('L\'email est obligatoire'); return; }
    if (!registerPassword || registerPassword.length < 6) { setRegisterError('Le mot de passe doit contenir au moins 6 caractères'); return; }
    if (!registerProfile.firstName) { setRegisterError('Le prénom est obligatoire'); return; }
    if (!registerProfile.lastName) { setRegisterError('Le nom est obligatoire'); return; }
    if (!registerProfile.nickname) { setRegisterError('Le surnom est obligatoire'); return; }
    if (!registerProfile.city) { setRegisterError('La ville est obligatoire'); return; }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(registerEmail)) { setRegisterError('Format d\'email invalide'); return; }

    try {
      await signup(registerEmail, registerPassword, registerProfile);
      onCloseRegister();
      // Reset form
      setRegisterEmail('');
      setRegisterPassword('');
      setRegisterProfile({
        firstName: '',
        lastName: '',
        nickname: '',
        city: '',
        team: 'Mercenaire Solitaire',
        notificationsEnabled: true
      });
    } catch (error: any) {
      console.error('Error registering:', error);
      if (error.code === 'auth/email-already-in-use') {
        setRegisterError('Cet email est déjà utilisé');
      } else if (error.code === 'auth/invalid-email') {
        setRegisterError('Format d\'email invalide');
      } else if (error.code === 'auth/weak-password') {
        setRegisterError('Le mot de passe est trop faible');
      } else {
        setRegisterError('Une erreur est survenue lors de l\'inscription');
      }
    }
  };

  return (
    <>
      {/* Dialogue de connexion */}
      <Dialog
        open={openLogin}
        onClose={onCloseLogin}
        maxWidth="xs"
        PaperProps={{
          sx: {
            backgroundColor: colors.background.dark,
            color: colors.text.primary,
            borderRadius: 2,
            width: '400px',
            margin: 'auto'
          }
        }}
      >
        <DialogTitle>Connexion</DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ mt: 2 }}>
            <Collapse in={resetEmailSent}>
              <Alert
                severity="success"
                sx={{ mb: 2 }}
                onClose={() => setResetEmailSent(false)}
              >
                Un email de réinitialisation a été envoyé à {loginEmail}.
                Veuillez vérifier votre boîte de réception et vos spams.
              </Alert>
            </Collapse>
            <TextField
              margin="dense"
              label="Email"
              type="email"
              fullWidth
              value={loginEmail}
              onChange={(e) => setLoginEmail(e.target.value)}
              required
              error={!!loginError}
              size="small"
              sx={{
                mb: 1,
                '& .MuiInputLabel-root': { color: colors.text.light }
              }}
            />
            <TextField
              margin="dense"
              label="Mot de passe"
              type={showPassword ? 'text' : 'password'}
              fullWidth
              value={loginPassword}
              onChange={(e) => setLoginPassword(e.target.value)}
              required
              error={!!loginError}
              helperText={loginError || (resetEmailSent && 'Un email de réinitialisation a été envoyé')}
              size="small"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiInputLabel-root': { color: colors.text.light }
              }}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', mt: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    sx={{ color: colors.button.primary }}
                  />
                }
                label="Se souvenir de moi"
                sx={{ color: colors.text.primary }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{
          p: 3, pt: 0, display: 'flex', flexDirection: 'column', gap: 1.5,
          '& .MuiButton-root': { margin: 0 }
        }}>
          <Button
            onClick={handleForgotPassword}
            variant="contained"
            fullWidth
            sx={{
              textTransform: 'none',
              backgroundColor: colors.button.info,
              color: colors.text.light,
              '&:hover': { backgroundColor: colors.button.infoHover }
            }}
          >
            Mot de passe oublié ?
          </Button>
          <Button
            onClick={() => {
              onCloseLogin();
              onOpenRegister();
            }}
            variant="contained"
            fullWidth
            sx={{
              backgroundColor: colors.button.primary,
              color: colors.text.light,
              '&:hover': { backgroundColor: colors.button.primaryHover }
            }}
          >
            CRÉER UN COMPTE
          </Button>
          <Button
            onClick={onCloseLogin}
            fullWidth
            sx={{
              color: colors.text.primary,
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
            }}
          >
            Annuler
          </Button>
          <Button
            onClick={handleLogin}
            variant="contained"
            fullWidth
            sx={{
              backgroundColor: colors.button.primary,
              color: colors.button.text,
              '&:hover': { backgroundColor: colors.button.primaryHover }
            }}
          >
            Se connecter
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialogue d'inscription */}
      <Dialog
        open={openRegister}
        onClose={onCloseRegister}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Inscription</DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ mt: 2 }}>
            {registerError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {registerError}
              </Alert>
            )}
            <TextField
              margin="dense"
              label="Prénom"
              fullWidth
              value={registerProfile.firstName}
              onChange={(e) => setRegisterProfile({ ...registerProfile, firstName: e.target.value })}
              required
            />
            <TextField
              margin="dense"
              label="Nom"
              fullWidth
              value={registerProfile.lastName}
              onChange={(e) => setRegisterProfile({ ...registerProfile, lastName: e.target.value })}
              required
            />
            <TextField
              margin="dense"
              label="Surnom"
              fullWidth
              value={registerProfile.nickname}
              onChange={(e) => setRegisterProfile({ ...registerProfile, nickname: e.target.value })}
              required
            />
            <TextField
              margin="dense"
              label="Email"
              type="email"
              fullWidth
              value={registerEmail}
              onChange={(e) => setRegisterEmail(e.target.value)}
              required
            />
            <TextField
              margin="dense"
              label="Mot de passe"
              type={showPassword ? 'text' : 'password'}
              fullWidth
              value={registerPassword}
              onChange={(e) => setRegisterPassword(e.target.value)}
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              margin="dense"
              label="Ville"
              fullWidth
              value={registerProfile.city}
              onChange={(e) => setRegisterProfile({ ...registerProfile, city: e.target.value })}
              required
            />
            <TextField
              margin="dense"
              label="Équipe"
              fullWidth
              value={registerProfile.team}
              onChange={(e) => setRegisterProfile({ ...registerProfile, team: e.target.value })}
              helperText="Laissez 'Mercenaire Solitaire' si vous n'avez pas d'équipe"
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={registerProfile.notificationsEnabled}
                  onChange={(e) => setRegisterProfile({ ...registerProfile, notificationsEnabled: e.target.checked })}
                  color="primary"
                />
              }
              label={
                <span style={{ fontSize: '0.9rem' }}>
                  Recevoir les emails des nouveaux événements
                </span>
              }
              sx={{ mt: 1, width: '100%' }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button onClick={onCloseRegister}>Annuler</Button>
          <Button
            onClick={handleRegister}
            variant="contained"
            color="primary"
          >
            CRÉER MON COMPTE
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
